import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { GameContext } from '../core/context';
import { Rng } from '../core/rng';
import type { System } from '../core/system';
import { qualityTier } from '../render/qualityTier';
import { BLAST_KILL_RADIUS_M, BLAST_KNOCK_RADIUS_M } from '../../shared/limits';
import { allowBlast, createBlastGate } from './vfxTuning';

/**
 * What a car going up looks like beyond the particles (docs/PERF.md "Effects"). Listens to `vehicleExploded`
 * (vehicles/vehicleSystem.ts applies the damage and impulse) and plays, every tier:
 *
 *  - the particle burst (fireball + smoke column on MEDIUM / HIGH through fx/quarksFx.ts, points on LOW)
 *    and the point-light flash
 *  - a shockwave ring on the ground: one additive quad swelling to ~14 m in 0.3 s
 *  - a ground light burst: an additive warm disc under the blast fading over half a second
 *  - pedestrians within BLAST_PED_R are knocked flat (closest ones killed), the ones further out cower; foot
 *    officers in the same radius go down too (offline; online the server floors its own units when the wreck's
 *    fuse runs out, server/police/force.ts blastOfficers), and the local
 *    player on foot is thrown and hurt (online as well: like a fall or ambient traffic, a local hurt the
 *    server confirms, so a remote player's blast floors you without a protocol change)
 *
 * and on MEDIUM / HIGH 8-12 low-poly debris chunks (door panel in the car's paint, a wheel, bumper bits),
 * instanced per kind, thrown out with gravity, a bounce and a fade. Everything is pooled at init.
 *
 * Budget: `allowBlast` (fx/vfxTuning.ts) gives at most two explosions per half second within 60 m the full
 * set; the rest (a roadblock chain reaction) get the ring, the burst, a small fireball and the flash.
 */
const RINGS = 4;
const BURSTS = 4;
const KIND_INSTANCES = 8;
const GROUND_Y = 0.135;
const RING_LIFE = 0.3;
const RING_RADIUS = 14;
const BURST_LIFE = 0.4;
const BURST_RADIUS = 8;
/** Pedestrians this close go down; closer than BLAST_KILL_R they do not get up. Further out, up to BLAST_COWER_R, they cower. */
const BLAST_PED_R = BLAST_KNOCK_RADIUS_M;
const BLAST_KILL_R = BLAST_KILL_RADIUS_M;
const BLAST_COWER_R = 24;
/** The local player on foot within BLAST_PED_R: down for this long and this much health, scaled by closeness. */
const BLAST_PLAYER_DOWN_S: readonly [number, number] = [0.8, 2.2];
const BLAST_PLAYER_HURT: readonly [number, number] = [8, 45];
const CHUNK_LIFE = 3.4;
const GRAVITY = 14;

function discTexture(size: number, paint: (g: CanvasRenderingContext2D, s: number) => void): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  paint(g, size);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** Vertex colours per face group: `faces` gives the colour of each geometry group (CylinderGeometry: side, top, bottom). */
function colourGroups(geo: THREE.BufferGeometry, faces: [number, number, number][]): THREE.BufferGeometry {
  const g = geo.toNonIndexed();
  const n = g.attributes.position!.count;
  const col = new Float32Array(n * 3);
  const groups = g.groups.length ? g.groups : [{ start: 0, count: n, materialIndex: 0 }];
  for (const grp of groups) {
    const c = faces[Math.min(faces.length - 1, grp.materialIndex ?? 0)]!;
    for (let i = grp.start; i < grp.start + grp.count; i++) col.set(c, i * 3);
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.clearGroups();
  return g;
}

/** A box whose big ±z faces are white (tinted by the instance colour: the car's paint) and whose edges are dark. */
function panelGeometry(): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(0.95, 0.6, 0.05).toNonIndexed();
  const n = g.attributes.position!.count;
  const col = new Float32Array(n * 3);
  const nor = g.attributes.normal!;
  for (let i = 0; i < n; i++) {
    const front = Math.abs(nor.getZ(i)) > 0.5;
    col.set(front ? [1, 1, 1] : [0.22, 0.2, 0.18], i * 3);
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.clearGroups();
  return g;
}

function wheelGeometry(): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(0.31, 0.31, 0.2, 10).rotateX(Math.PI / 2);
  return colourGroups(g, [
    [0.07, 0.07, 0.07],
    [0.55, 0.55, 0.52],
    [0.55, 0.55, 0.52],
  ]);
}

function bumperGeometry(): THREE.BufferGeometry {
  const bar = (len: number, x: number, z: number, yaw: number): THREE.BufferGeometry =>
    new THREE.BoxGeometry(len, 0.12, 0.08).rotateY(yaw).translate(x, 0, z);
  const g = mergeGeometries([bar(0.7, 0, 0, 0), bar(0.3, 0.45, 0.12, -0.7), bar(0.3, -0.45, 0.12, 0.7)])!;
  return colourGroups(g, [[0.78, 0.78, 0.75]]);
}

interface Chunks {
  mesh: THREE.InstancedMesh;
  /** Half height for the ground bounce. */
  radius: number;
  pos: Float32Array;
  vel: Float32Array;
  rot: Float32Array;
  spin: Float32Array;
  age: Float32Array;
}

export function createExplosionFxSystem(): System {
  let ctx: GameContext;
  const rng = new Rng(0xb00f);
  const gate = createBlastGate();
  const disposables: { dispose(): void }[] = [];
  const objects: THREE.Object3D[] = [];
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const v = new THREE.Vector3();
  const s = new THREE.Vector3();
  const col = new THREE.Color();
  const zero = new THREE.Matrix4().makeScale(0, 0, 0);

  let ring: THREE.InstancedMesh | null = null;
  const ringAge = new Float32Array(RINGS).fill(-1);
  const ringAt = new Float32Array(RINGS * 2);
  let ringNext = 0;
  let burst: THREE.InstancedMesh | null = null;
  const burstAge = new Float32Array(BURSTS).fill(-1);
  const burstAt = new Float32Array(BURSTS * 2);
  let burstNext = 0;
  const kinds: Chunks[] = [];
  let anyChunks = false;

  function groundDisc(tex: THREE.Texture, count: number, name: string, order: number): THREE.InstancedMesh {
    const mat = new THREE.MeshBasicMaterial({ map: tex, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false });
    const plane = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    const mesh = new THREE.InstancedMesh(plane, mat, count);
    for (let i = 0; i < count; i++) {
      mesh.setMatrixAt(i, zero);
      mesh.setColorAt(i, col.setRGB(0, 0, 0));
    }
    mesh.frustumCulled = false;
    mesh.visible = false;
    mesh.name = name;
    mesh.renderOrder = order;
    disposables.push(mat, plane, mesh);
    return mesh;
  }

  function makeChunks(geo: THREE.BufferGeometry, radius: number, name: string): Chunks {
    const mat = new THREE.MeshLambertMaterial({ vertexColors: true });
    const mesh = new THREE.InstancedMesh(geo, mat, KIND_INSTANCES);
    for (let i = 0; i < KIND_INSTANCES; i++) {
      mesh.setMatrixAt(i, zero);
      mesh.setColorAt(i, col.setRGB(1, 1, 1));
    }
    mesh.frustumCulled = false;
    mesh.visible = false;
    mesh.name = name;
    disposables.push(mat, geo, mesh);
    const c: Chunks = {
      mesh,
      radius,
      pos: new Float32Array(KIND_INSTANCES * 3),
      vel: new Float32Array(KIND_INSTANCES * 3),
      rot: new Float32Array(KIND_INSTANCES * 3),
      spin: new Float32Array(KIND_INSTANCES * 3),
      age: new Float32Array(KIND_INSTANCES).fill(-1),
    };
    kinds.push(c);
    return c;
  }

  function throwChunk(c: Chunks, x: number, z: number, paint: number): void {
    // the oldest slot goes if every one is live
    let i = -1;
    let oldest = -1;
    for (let k = 0; k < KIND_INSTANCES; k++) {
      if (c.age[k]! < 0) {
        i = k;
        break;
      }
      if (c.age[k]! > oldest) {
        oldest = c.age[k]!;
        i = k;
      }
    }
    if (i < 0) return;
    const a = rng.range(0, Math.PI * 2);
    const sp = rng.range(3.5, 9);
    c.pos[i * 3] = x + Math.cos(a) * 0.6;
    c.pos[i * 3 + 1] = 0.9;
    c.pos[i * 3 + 2] = z + Math.sin(a) * 0.6;
    c.vel[i * 3] = Math.cos(a) * sp;
    c.vel[i * 3 + 1] = rng.range(5, 10);
    c.vel[i * 3 + 2] = Math.sin(a) * sp;
    for (let k = 0; k < 3; k++) {
      c.rot[i * 3 + k] = rng.range(0, Math.PI * 2);
      c.spin[i * 3 + k] = rng.range(-9, 9);
    }
    c.age[i] = 0;
    c.mesh.setColorAt(i, col.setHex(paint));
    c.mesh.instanceColor!.needsUpdate = true;
    anyChunks = true;
  }

  function startRing(x: number, z: number): void {
    const i = ringNext;
    ringNext = (ringNext + 1) % RINGS;
    ringAge[i] = 0;
    ringAt[i * 2] = x;
    ringAt[i * 2 + 1] = z;
    const j = burstNext;
    burstNext = (burstNext + 1) % BURSTS;
    burstAge[j] = 0;
    burstAt[j * 2] = x;
    burstAt[j * 2 + 1] = z;
  }

  function knockPeds(x: number, z: number, byPlayer: boolean): void {
    const peds = ctx.services.tryGet('peds');
    if (!peds) return;
    // collect first: knocking one down changes the hash while it iterates
    const ids: number[] = [];
    const px: number[] = [];
    const pz: number[] = [];
    peds.forEachNear(x, z, BLAST_PED_R, (ax, az, id) => {
      if (Math.hypot(ax - x, az - z) <= BLAST_PED_R) {
        ids.push(id);
        px.push(ax);
        pz.push(az);
      }
    });
    for (let i = 0; i < ids.length; i++) {
      const dx = px[i]! - x;
      const dz = pz[i]! - z;
      const d = Math.hypot(dx, dz) || 0.5;
      const fall = 1 - d / BLAST_PED_R;
      const speed = 4 + 9 * fall;
      peds.knockDown(ids[i]!, { x: (dx / d) * speed, z: (dz / d) * speed }, null, byPlayer, { lethal: d < BLAST_KILL_R });
    }
    peds.cower({ x, z }, BLAST_COWER_R, 2.5, 0.5);
  }

  /** How hard a blast at distance `d` throws a body (m/s along the way out) and how close it was (0..1). */
  function blastPush(dx: number, dz: number): { x: number; z: number; fall: number; d: number } {
    const d = Math.hypot(dx, dz) || 0.5;
    const fall = Math.max(0, 1 - d / BLAST_PED_R);
    const speed = 4 + 9 * fall;
    return { x: (dx / d) * speed, z: (dz / d) * speed, fall, d };
  }

  function knockOfficers(x: number, z: number, byPlayer: boolean): void {
    const police = ctx.services.tryGet('police');
    if (!police) return;
    for (const u of police.units()) {
      if (u.kind !== 'foot' || u.role === 'disabled') continue;
      const p = blastPush(u.x - x, u.z - z);
      if (p.d > BLAST_PED_R) continue;
      police.strikeOfficer(u.id, p, p.d < BLAST_KILL_R, byPlayer);
    }
  }

  function knockPlayer(x: number, z: number): void {
    const player = ctx.services.tryGet('player');
    if (!player || (player.mode !== 'foot' && player.mode !== 'knocked')) return;
    const p = blastPush(player.pos.x - x, player.pos.z - z);
    if (p.d > BLAST_PED_R) return;
    const [s0, s1] = BLAST_PLAYER_DOWN_S;
    const [h0, h1] = BLAST_PLAYER_HURT;
    player.knockDown(s0 + (s1 - s0) * p.fall, p);
    ctx.events.emit('playerHurt', { amount: Math.round(h0 + (h1 - h0) * p.fall), cause: 'explosion', pos: { x: player.pos.x, z: player.pos.z } });
  }

  function onExploded(ev: { vehicleId: number; pos: { x: number; z: number }; byPlayer: boolean }): void {
    const { x, z } = ev.pos;
    const full = allowBlast(gate, ctx.state.time, x, z);
    startRing(x, z);
    knockPeds(x, z, ev.byPlayer);
    knockOfficers(x, z, ev.byPlayer);
    knockPlayer(x, z);
    const particles = ctx.services.tryGet('particles');
    if (particles) {
      const p3 = { x, y: 0.8, z };
      if (full) {
        particles.emit('explosion', p3, { count: 70, intensity: 1.2 });
        particles.emit('blackSmoke', p3, { count: 30, intensity: 1.2 });
        particles.emit('debris', p3, { count: 30, intensity: 1.3 });
        particles.emit('sparks', p3, { count: 40, intensity: 1.5 });
        particles.flash(p3, 900, 0.7);
      } else {
        particles.emit('explosion', p3, { count: 24, intensity: 0.8 });
        particles.emit('sparks', p3, { count: 12, intensity: 1.2 });
        particles.flash(p3, 500, 0.5);
      }
    }
    if (full && kinds.length && qualityTier() !== 'low') {
      const paint = ctx.services.tryGet('vehicles')?.get(ev.vehicleId)?.paint ?? 0x8a8a8a;
      const [panel, wheel, bumper] = kinds as [Chunks, Chunks, Chunks];
      const n = 4 + Math.floor(rng.next() * 3);
      for (let i = 0; i < n; i++) throwChunk(panel, x, z, paint);
      for (let i = 0; i < 2 + Math.floor(rng.next() * 2); i++) throwChunk(wheel, x, z, 0xffffff);
      for (let i = 0; i < 2 + Math.floor(rng.next() * 2); i++) throwChunk(bumper, x, z, 0xffffff);
    }
  }

  return {
    id: 'explosion-fx',
    order: 726,
    init(c) {
      ctx = c;
      c.events.on('vehicleExploded', onExploded);
      if (!c.render) return;

      const ringTex = discTexture(128, (g, n) => {
        // a soft band near the edge: the pressure front
        const gr = g.createRadialGradient(n / 2, n / 2, 0, n / 2, n / 2, n / 2);
        gr.addColorStop(0.62, 'rgba(255,255,255,0)');
        gr.addColorStop(0.8, 'rgba(255,255,255,0.9)');
        gr.addColorStop(0.9, 'rgba(255,255,255,0.5)');
        gr.addColorStop(1, 'rgba(255,255,255,0)');
        g.fillStyle = gr;
        g.fillRect(0, 0, n, n);
      });
      const burstTex = discTexture(128, (g, n) => {
        const gr = g.createRadialGradient(n / 2, n / 2, 0, n / 2, n / 2, n / 2);
        gr.addColorStop(0, 'rgba(255,255,255,1)');
        gr.addColorStop(0.3, 'rgba(255,255,255,0.55)');
        gr.addColorStop(1, 'rgba(255,255,255,0)');
        g.fillStyle = gr;
        g.fillRect(0, 0, n, n);
      });
      disposables.push(ringTex, burstTex);
      ring = groundDisc(ringTex, RINGS, 'fx-shockwave', 5);
      burst = groundDisc(burstTex, BURSTS, 'fx-ground-burst', 4);
      objects.push(ring, burst);

      const panel = makeChunks(panelGeometry(), 0.3, 'fx-chunk-panel');
      const wheel = makeChunks(wheelGeometry(), 0.31, 'fx-chunk-wheel');
      const bumper = makeChunks(bumperGeometry(), 0.06, 'fx-chunk-bumper');
      objects.push(panel.mesh, wheel.mesh, bumper.mesh);
      c.scene.add(...objects);
    },
    update(dt) {
      if (dt <= 0) return;
      if (ring) {
        let live = false;
        for (let i = 0; i < RINGS; i++) {
          if (ringAge[i]! < 0) continue;
          ringAge[i] = ringAge[i]! + dt;
          const t = ringAge[i]! / RING_LIFE;
          if (t >= 1) {
            ringAge[i] = -1;
            ring.setMatrixAt(i, zero);
            continue;
          }
          live = true;
          const r = RING_RADIUS * (1 - (1 - t) * (1 - t));
          m.makeScale(r * 2, 1, r * 2).setPosition(ringAt[i * 2]!, GROUND_Y + 0.02, ringAt[i * 2 + 1]!);
          ring.setMatrixAt(i, m);
          const a = (1 - t) * 0.7;
          ring.setColorAt(i, col.setRGB(a, a * 0.78, a * 0.5));
        }
        ring.visible = live;
        ring.instanceMatrix.needsUpdate = true;
        ring.instanceColor!.needsUpdate = true;
      }
      if (burst) {
        let live = false;
        for (let i = 0; i < BURSTS; i++) {
          if (burstAge[i]! < 0) continue;
          burstAge[i] = burstAge[i]! + dt;
          const t = burstAge[i]! / BURST_LIFE;
          if (t >= 1) {
            burstAge[i] = -1;
            burst.setMatrixAt(i, zero);
            continue;
          }
          live = true;
          const r = BURST_RADIUS * (0.6 + 0.4 * t);
          m.makeScale(r * 2, 1, r * 2).setPosition(burstAt[i * 2]!, GROUND_Y + 0.015, burstAt[i * 2 + 1]!);
          burst.setMatrixAt(i, m);
          const a = (1 - t) * (1 - t) * 0.7;
          burst.setColorAt(i, col.setRGB(a, a * 0.62, a * 0.3));
        }
        burst.visible = live;
        burst.instanceMatrix.needsUpdate = true;
        burst.instanceColor!.needsUpdate = true;
      }
      if (!anyChunks) return;
      anyChunks = false;
      for (const c of kinds) {
        let live = false;
        for (let i = 0; i < KIND_INSTANCES; i++) {
          if (c.age[i]! < 0) continue;
          c.age[i] = c.age[i]! + dt;
          if (c.age[i]! >= CHUNK_LIFE) {
            c.age[i] = -1;
            c.mesh.setMatrixAt(i, zero);
            continue;
          }
          live = true;
          const o = i * 3;
          c.vel[o + 1] = c.vel[o + 1]! - GRAVITY * dt;
          c.pos[o] = c.pos[o]! + c.vel[o]! * dt;
          c.pos[o + 1] = c.pos[o + 1]! + c.vel[o + 1]! * dt;
          c.pos[o + 2] = c.pos[o + 2]! + c.vel[o + 2]! * dt;
          const floor = GROUND_Y + c.radius;
          if (c.pos[o + 1]! < floor) {
            c.pos[o + 1] = floor;
            if (c.vel[o + 1]! < -1) {
              c.vel[o + 1] = -c.vel[o + 1]! * 0.35;
              c.vel[o] = c.vel[o]! * 0.6;
              c.vel[o + 2] = c.vel[o + 2]! * 0.6;
              for (let k = 0; k < 3; k++) c.spin[o + k] = c.spin[o + k]! * 0.5;
            } else {
              c.vel[o + 1] = 0;
              const f = Math.max(0, 1 - 2.5 * dt);
              c.vel[o] = c.vel[o]! * f;
              c.vel[o + 2] = c.vel[o + 2]! * f;
              for (let k = 0; k < 3; k++) c.spin[o + k] = c.spin[o + k]! * f;
            }
          }
          for (let k = 0; k < 3; k++) c.rot[o + k] = c.rot[o + k]! + c.spin[o + k]! * dt;
          // the last 0.6 s shrink away
          const left = CHUNK_LIFE - c.age[i]!;
          const sc = left < 0.6 ? left / 0.6 : 1;
          e.set(c.rot[o]!, c.rot[o + 1]!, c.rot[o + 2]!);
          q.setFromEuler(e);
          m.compose(v.set(c.pos[o]!, c.pos[o + 1]!, c.pos[o + 2]!), q, s.set(sc, sc, sc));
          c.mesh.setMatrixAt(i, m);
        }
        c.mesh.visible = live;
        c.mesh.instanceMatrix.needsUpdate = true;
        if (live) anyChunks = true;
      }
    },
    dispose() {
      ctx.events.off('vehicleExploded', onExploded);
      if (objects.length) ctx.scene.remove(...objects);
      for (const d of disposables) d.dispose();
    },
  };
}
